import { createContext, useContext, useState, useEffect } from "react";
import { getProducts, getCategories } from "../firebase/productService";
import {
  products as staticProducts,
  categories as staticCategories,
} from "../data/products";

const ProductContext = createContext();

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error("useProducts must be used within a ProductProvider");
  }
  return context;
};

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState(
    staticProducts.map((p) => ({ ...p, id: `static-${p.id}` })),
  );
  const [categories, setCategories] = useState(staticCategories);
  const [isLoading, setIsLoading] = useState(true);

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const [firebaseProducts, firebaseCategories] = await Promise.all([
        getProducts(),
        getCategories(),
      ]);

      // Combine Firebase products with static products (Firebase first)
      // Give Firebase products unique IDs to avoid conflicts
      const combinedProducts = [
        ...firebaseProducts.map((p, index) => ({
          ...p,
          id: p.id, // Keep Firebase document ID
        })),
        ...staticProducts.map((p) => ({
          ...p,
          id: `static-${p.id}`, // Prefix static product IDs
        })),
      ];

      setProducts(combinedProducts);
      setCategories(
        firebaseCategories.length > 0 ? firebaseCategories : staticCategories,
      );
    } catch (error) {
      console.error("Error fetching products:", error);
      // Fall back to static data on error
      setProducts(staticProducts);
      setCategories(staticCategories);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const getProductById = (id) => {
    return products.find((p) => String(p.id) === String(id));
  };

  const getProductsByCategory = (categoryId) => {
    return products.filter((p) => p.category === categoryId);
  };

  return (
    <ProductContext.Provider
      value={{
        products,
        categories,
        isLoading,
        refreshProducts: fetchProducts,
        getProductById,
        getProductsByCategory,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};
